/**
 * Action kind: cart.
 *
 * Two visual states of ONE action, chosen by state.cartQty — not two kinds:
 *   qty === 0  →  "Add to Cart" button
 *   qty  >  0  →  −/input/+ stepper + inline go-to-cart link
 *
 * Class names are the production contract from raftor_webhq's grid.js and
 * listing_quantity.js, so webshop's bind_add_to_cart_action() and the cart
 * store keep working with no changes:
 *   .btn-add-to-cart-list[data-item-code]  .go-to-cart-grid
 *   .listing-qty-wrapper[data-item-code]   .cart-qty
 *   .number-spinner  .cart-btn[data-dir]   .listing-go-to-cart
 *
 * Renders only. Click handling is delegated by the grid — no listeners here.
 */

import { el, svg } from '../../dom.js';

const CART_ICON =
	'<circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>' +
	'<path d="M1 1h4l2.7 13.4a2 2 0 0 0 2 1.6h9.7a2 2 0 0 0 2-1.6L23 6H6"/>';

// rfhq-pc-cta--outline: same style/theme treatment as explore.js's button -
// user correction, Add to Cart should not have its own separate filled look.
// rfhq_product_card.css's .btn-add-to-cart-list rules (incl. the !important
// ones beating core's v15 primary-colour clobber) apply the same outline
// values directly too, since those need !important and a class-order win
// alone wouldn't survive that - this class is kept alongside for the shared
// contract to stay visible in the DOM, not because it's load-bearing alone.
function addButton(model) {
	const a = model.actions;
	return el(
		'div',
		{
			class: 'btn btn-sm btn-primary btn-add-to-cart-list w-100 mt-2 rfhq-pc-cta rfhq-pc-cta--outline',
			dataset: { itemCode: model.itemCode },
			role: 'button',
			tabindex: '0',
			text: a.addLabel || 'Add to Cart',
		}
	);
}

function gotoButton(model) {
	const a = model.actions;
	return el('a', { href: a.gotoHref || '/cart' }, [
		el('div', {
			class: 'btn btn-sm btn-primary btn-add-to-cart-list w-100 mt-4 go-to-cart-grid rfhq-pc-cta rfhq-pc-cta--outline',
			dataset: { itemCode: model.itemCode },
			text: a.gotoLabel || 'Go to Cart',
		}),
	]);
}

function stepper(model) {
	const qty = model.state.cartQty;

	return el(
		'div',
		{
			class: 'listing-qty-wrapper mt-1 rfhq-pc-qty',
			dataset: { itemCode: model.itemCode },
		},
		[
			el('div', { class: 'd-flex align-items-center listing-qty-inner' }, [
				el('div', { class: 'input-group number-spinner rfhq-pc-qty-spinner' }, [
					el('span', { class: 'input-group-prepend d-sm-inline-block' }, [
						el('button', {
							class: 'btn cart-btn rfhq-pc-qty-btn',
							type: 'button',
							'data-dir': 'dwn',
							'aria-label': 'Decrease quantity',
							html: '&minus;',
						}),
					]),
					el('input', {
						class: 'form-control text-center cart-qty rfhq-pc-qty-input',
						value: String(qty),
						inputmode: 'numeric',
						'aria-label': 'Quantity',
						dataset: { itemCode: model.itemCode, syncedQty: String(qty), pendingUpdate: '0' },
					}),
					el('span', { class: 'input-group-append d-sm-inline-block' }, [
						el('button', {
							class: 'btn cart-btn rfhq-pc-qty-btn',
							type: 'button',
							'data-dir': 'up',
							'aria-label': 'Increase quantity',
							text: '+',
						}),
					]),
				]),
				el(
					'a',
					{
						href: model.actions.gotoHref || '/cart',
						class: 'listing-go-to-cart ml-2 rfhq-pc-qty-cart',
						'aria-label': 'Go to cart',
					},
					[svg(CART_ICON, { class: 'icon sm' })]
				),
			]),
		]
	);
}

export function render(model) {
	if (!model.itemCode) return null; // INVARIANT 2 — enforced upstream, belt-and-braces here

	const frag = document.createDocumentFragment();

	if (model.state.cartQty > 0) {
		frag.appendChild(stepper(model));
	} else {
		frag.appendChild(addButton(model));
		// Rendered hidden, exactly as core does: the pair toggles by class so
		// the binder can flip states without a re-render.
		const goto = gotoButton(model);
		goto.firstChild.classList.add('hidden');
		frag.appendChild(goto);
	}

	return frag;
}
